/**
 * @file: WiredComposer.tsx
 *
 * The whole bottom compose area, wired to its own state. It owns the
 * composer's state (`useChatComposer`: draft, typing throttle, voice
 * recorder, attachment menu, AI composer — composing the keyboard inset and
 * mention picker) and subscribes only the two shared slices it reads off the
 * conversation context: the reply target and the mention candidates. A keystroke
 * re-renders this subtree, not the conversation.
 *
 * Layout: the input strip pads its own bottom by the keyboard inset (+ the
 * home-indicator safe area), so the composer — and the reply bar above it —
 * rise with the keyboard while the message list shrinks to fit.
 */

import { Typography } from '@euler/app/components/typography';
import { useChatConversation } from '@euler/app/flows/chat/lib/conversation/context';
import { isGroupRoom } from '@euler/app/flows/chat/lib/conversation/handle';
import { useChatComposer } from '@euler/app/flows/chat/lib/conversation/hooks/useChatComposer';
import { useChatSession } from '@euler/app/flows/chat/lib/session/context';
import { mentionCandidatesFromMembers } from '@euler/app/flows/chat/utils/mentions';
import { BottomPanel } from '@euler/components/BottomPanel';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useSystemMetrics } from '@euler/functions/useSystemMetrics';
import { useBehaviorSubject } from '@euler/utils/hooks';
import { Pencil, X } from 'lucide-react-native';
import { FC, memo, useCallback, useEffect, useMemo } from 'react';
import {
  LayoutChangeEvent,
  StyleSheet,
  TouchableOpacity,
  View,
} from 'react-native';
import { ContactPickerPanel } from '../ContactPickerPanel';
import { AIResponseComposer } from '../ai-composer/AIResponseComposer';
import { ReplyPreviewBar } from '../conversation/ReplyPreviewBar';
import { ChatComposer } from './ChatComposer';
import { MediaSendStylePanel } from './MediaSendStylePanel';
import { MentionPickerPanel } from './MentionPickerPanel';

export const WiredComposer: FC = memo(() => {
  const { safeAreaInsets } = useSystemMetrics();
  const { me } = useChatSession();
  const conversation = useChatConversation();
  const composer = useChatComposer();
  const { quoteTarget$ } = conversation.coordinator;
  const [quoteTarget] = useBehaviorSubject(quoteTarget$);
  // Mention candidates are derived from the member list (sans the viewer);
  // `@all` is offered by the panel itself.
  const [members] = useBehaviorSubject(conversation.members$);
  const mentionCandidates = useMemo(
    () => mentionCandidatesFromMembers(members, me.platformUserId),
    [members, me],
  );

  // Tapping a bot's suggested-question chip sends it back to the bot, with
  // the bot auto @-mentioned (so the send writer wakes it again).
  const { sendPayload } = conversation;
  const { suggestQuestionRequested$ } = conversation.coordinator;
  useEffect(() => {
    const sub = suggestQuestionRequested$.subscribe(question => {
      const bot = members.find(m => m.type === 'bot' && !!m.botId);
      if (!bot?.botId) return;
      const name = bot.displayName || bot.name || '服务助手';
      const token = `@${name}`;
      void sendPayload({
        type: 'text',
        text: `${token} ${question}`,
        mentions: [
          {
            range: [0, token.length] as [number, number],
            target: { kind: 'bot' as const, botId: bot.botId },
            displayName: name,
          },
        ],
      });
    });
    return () => sub.unsubscribe();
  }, [suggestQuestionRequested$, members, sendPayload]);

  const clearQuote = useCallback(() => quoteTarget$.next(null), [quoteTarget$]);

  const { inputBarHeight$ } = conversation.coordinator.scroll;
  const onComposerLayout = useCallback(
    (e: LayoutChangeEvent) => inputBarHeight$.next(e.nativeEvent.layout.height),
    [inputBarHeight$],
  );

  return (
    <>
      {composer.editingMessage ? (
        <EditingBar onCancel={composer.cancelEdit} />
      ) : quoteTarget ? (
        <ReplyPreviewBar target={quoteTarget} onClear={clearQuote} />
      ) : null}
      {/*
       * Unified bottom strip: input bar + safe-area inset + keyboard inset in
       * one container with a single background colour so the home-indicator
       * strip matches the input bar. `onLayout` measures the WHOLE strip so
       * the floating pill's bottomOffset clears it.
       */}
      <View
        onLayout={onComposerLayout}
        style={{
          backgroundColor: '#fafafa',
          paddingBottom: safeAreaInsets.bottom + composer.keyboardInset,
        }}
      >
        <ChatComposer
          ref={composer.composerRef}
          text={composer.text}
          mentionEnabled={isGroupRoom(conversation.conversationHandle)}
          mentions={composer.mentions}
          onMentionTrigger={composer.onMentionTrigger}
          onChangeText={composer.onChangeText}
          onSendText={composer.sendMessage}
          sending={composer.sending}
          onPressVoice={composer.onPressVoice}
          onSendSpoken={composer.sendSpokenText}
          onSelectAttachment={composer.onSelectAttachment}
          recording={composer.voiceActive}
          meter$={composer.voiceMeter$}
          onCancelVoice={composer.onCancelVoice}
          onCompleteVoice={composer.onCompleteVoice}
        />
      </View>
      <AIResponseComposer
        visible={composer.aiComposerVisible}
        onClose={composer.closeAIComposer}
        onSend={composer.sendAIResponse}
      />
      <BottomPanel
        visible={composer.mentionPickerOpen}
        onClose={composer.dismissMentionPicker}
      >
        <MentionPickerPanel
          candidates={mentionCandidates}
          includeAll
          onSelect={composer.onPickMention}
        />
      </BottomPanel>
      <MediaSendStylePanel
        visible={composer.mediaStyleVisible}
        assetCount={composer.mediaStyleAssetCount}
        onClose={composer.onCloseMediaStylePanel}
        onConfirm={composer.onConfirmMediaStyle}
      />
      <ContactPickerPanel
        visible={composer.contactPickerVisible}
        onClose={composer.closeContactPicker}
        onSelect={composer.sendContactCard}
      />
    </>
  );
});

/** The strip above the composer while editing a message, with an `×` to exit. */
const EditingBar: FC<{ onCancel: () => void }> = memo(({ onCancel }) => {
  const { rpx } = useResponsiveContext();
  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: rpx(12),
        paddingVertical: rpx(8),
        backgroundColor: '#f3f4f6',
        borderTopWidth: StyleSheet.hairlineWidth,
        borderTopColor: '#e0e0e0',
      }}
    >
      <Pencil color="#3b82f6" size={rpx(14)} strokeWidth={2} />
      <Typography.Text
        size={rpx(12)}
        color="#3b82f6"
        style={{ marginLeft: rpx(8), fontFamily: FontFamily.NotoSans.Medium }}
      >
        编辑消息
      </Typography.Text>
      <View style={{ flex: 1 }} />
      <TouchableOpacity
        onPress={onCancel}
        hitSlop={{ top: 8, right: 8, bottom: 8, left: 8 }}
      >
        <X color="#9ca3af" size={rpx(16)} />
      </TouchableOpacity>
    </View>
  );
});
